import { Action as _Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import * as IConfirm from '../../../../../interfaces/merchants/products/purchases/confirm';
import * as ICustomFields from '../../../../../interfaces/merchants/products/purchases/custom_fields';
import * as IUseCase from '../../../../../interfaces/merchants/products/purchases/use_case';

import * as $ from './helper';

export type Result<R> = ThunkAction<R, IPurchases.State, undefined, Actions>;
export type Dispatch = ThunkDispatch<IPurchases.State, undefined, Actions>;

export enum Types {
  Init = 'merchant/services/products/purchases/init',
  SetStep = 'merchant/services/products/purchases/setstep',
  SetPostable = 'merchant/services/products/purchases/setpostable',
  SetStripeErrors = 'merchant/services/products/purchases/setpaymenterrors',
  SetCustomFields = 'merchant/services/products/purchases/setcustomfields',
  SetCustomerErrors = 'merchant/services/products/purchases/setcustomererrors',
  SetCustomFieldsAndErrors = 'merchant/services/products/purchases/setcustomfieldsanderrors',
  SetEntireErrors = 'merchant/services/products/purchases/setentirederrors',
  SetVerifiedUser = 'merchant/services/products/purchases/setuserverified',
  ClearCustomerError = 'merchant/services/products/purchases/clearcustomererrors',
}

export interface InitAction extends _Action {
  type: Types.Init;
  payload: {
    publicId: string;
    productId: string;
    loggedIn: boolean;
    subscribed: boolean;
    productData: IPurchases.MerchantProductData;
    dataSet: DataSet;
  };
}

export interface SetStepAction extends _Action {
  type: Types.SetStep;
  payload: { step: number; renew?: boolean };
}

export interface SetPostableAction extends _Action {
  type: Types.SetPostable;
  payload: {
    postable: boolean;
  };
}

export interface SetStripeErrorsAction extends _Action {
  type: Types.SetStripeErrors;
  payload: { errors: stripe.StripeError };
}

export interface SetCustomFieldsAction extends _Action {
  type: Types.SetCustomFields;
  payload: {
    customFields: ICustomFields.MerchantPurchaseCustomFieldsData;
  };
}

export interface SetCustomerErrorsAction extends _Action {
  type: Types.SetCustomerErrors;
  payload: {
    errors: IPurchases.PurchasingCustomerCustomFieldsError;
  };
}

export interface SetCustomFieldsAndErrorsAction extends _Action {
  type: Types.SetCustomFieldsAndErrors;
  payload: {
    customFields: ICustomFields.MerchantPurchaseCustomFieldsData;
    errors: IPurchases.PurchasingCustomerCustomFieldsError;
  };
}

export interface SetEntireErrorsAction extends _Action {
  type: Types.SetEntireErrors;
  payload: {
    errors: IPurchases.EntireErrorType[];
  };
}

export interface SetVerifiedUserAction extends _Action {
  type: Types.SetVerifiedUser;
  payload: {
    verifiedUser: boolean;
  };
}

export interface ClearCustomerErrorsAction extends _Action {
  type: Types.ClearCustomerError;
  payload: {
    id: number;
  };
}

export type Actions =
  | InitAction
  | SetStepAction
  | SetPostableAction
  | SetStripeErrorsAction
  | SetCustomFieldsAction
  | SetCustomerErrorsAction
  | SetCustomFieldsAndErrorsAction
  | SetEntireErrorsAction
  | SetVerifiedUserAction
  | ClearCustomerErrorsAction;

export type DataSet = DataSetSubscription | DataSetTicketBook;

interface DataSetSubscription {
  schemeType: IPurchases.SchemeType.Subscription;
  customFieldsData: ICustomFields.MerchantPurchaseCustomFieldsData;
  confirmData: IConfirm.MerchantPurchaseConfirmData;
  useCaseData: IUseCase.MerchantPurchasedUseCaseData;
}

interface DataSetTicketBook {
  schemeType: IPurchases.SchemeType.TicketBook;
  customFieldsData: ICustomFields.MerchantPurchaseCustomFieldsData;
  confirmData: IConfirm.MerchantPurchaseConfirmData;
  useCaseData: IUseCase.MerchantPurchasedUseCaseData;
}

const PAYLOAD_TO_STATE_CONVERTERS: {
  [schemeType in IPurchases.SchemeType]: (
    dataSet: DataSet,
  ) => IPurchases.Settings | undefined;
} = {
  [IPurchases.SchemeType.Subscription]: (dataSet: DataSetSubscription) =>
    $.toSettingsSchemeTypeSubscription(dataSet),
  [IPurchases.SchemeType.TicketBook]: (dataSet: DataSetTicketBook) =>
    $.toSettingsSchemeTypeTicketBook(dataSet),
};

export default (
  state: IPurchases.State = {},
  action: Actions,
): IPurchases.State => {
  switch (action.type) {
    case Types.Init: {
      const {
        publicId,
        productId,
        productData,
        dataSet,
        loggedIn,
        subscribed,
      } = action.payload;
      const converter = PAYLOAD_TO_STATE_CONVERTERS[dataSet.schemeType];
      if (!converter) {
        throw new Error(
          `please defined a converter for modelType: "${dataSet.schemeType}"`,
        );
      }

      const merchantProduct = $.toMerchantProduct(productData);

      return {
        merchantProduct,
        publicId,
        productId,
        settings: converter(dataSet),
        step: $.toStep(dataSet.schemeType, loggedIn, subscribed),
        postable: true,
      };
    }
    case Types.SetStep: {
      return {
        ...state,
        step: action.payload.step,
        renew: action.payload.renew,
      };
    }
    case Types.SetPostable: {
      return {
        ...state,
        postable: action.payload.postable,
      };
    }
    case Types.SetStripeErrors: {
      return {
        ...state,
        settings: $.attachPaymentErrors(state.settings!, action.payload.errors),
      };
    }
    case Types.SetCustomFields: {
      const settings = state.settings;
      const customer = $.toSettingCustomer(action.payload.customFields);
      return {
        ...state,
        settings: {
          ...settings!,
          customer,
        },
      };
    }
    case Types.SetCustomerErrors: {
      return {
        ...state,
        settings: $.attachCustomerErrors(
          state.settings!,
          action.payload.errors,
        ),
      };
    }
    case Types.SetCustomFieldsAndErrors: {
      const settings = state.settings;
      const customer = $.toSettingCustomerAndErrors(
        action.payload.customFields,
        action.payload.errors,
      );
      return {
        ...state,
        settings: {
          ...settings!,
          customer,
        },
      };
    }
    case Types.SetEntireErrors: {
      const settings = state.settings;
      return {
        ...state,
        settings: {
          ...settings!,
          completion: {
            ...settings!.completion,
            entireErrors: action.payload.errors,
          },
        },
      };
    }
    case Types.SetVerifiedUser: {
      const settings = state.settings;
      return {
        ...state,
        settings: {
          ...settings!,
          completion: {
            ...settings!.completion,
            verifiedUser: action.payload.verifiedUser,
          },
        },
      };
    }
    case Types.ClearCustomerError: {
      return {
        ...state,
        settings: $.dettachCustomerErrors(state.settings!, action.payload.id),
      };
    }
    default:
      return state;
  }
};
